import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  BG_ATMOSPHERE, VIGNETTE, DIALOG_STYLE,
  FourCorners,
} from '../lib/medievalTheme';
import { getLocalApiOrigin } from '../utils/localApi';
import { t } from '../localization/t';

/**
 * Blocking dialog that runs after login when the server cosmetics
 * catalog ships token art versions newer than what's on disk. We hand
 * the stale list to `/api/assets/download`, which fetches each from
 * Vercel Blob and writes it to the persistent cache under the
 * versioned filename (`<slug>.v<N>.webp`). Subsequent renders read
 * from disk via the runtime image handler, which never touches Blob
 * itself. That fallback was removed once gates became authoritative.
 * The gate cannot be dismissed; the only exit is "all done" or "give
 * up after too many failures" so partially-updated art can't reach
 * gameplay.
 *
 * Sized and styled to match FirstRunDownload so the visual language
 * is consistent. Same logo, same progress bar, same medieval frame.
 */
export default class TokenUpdateGate extends Component {
  state = {
    // 'downloading' is the only non-terminal phase; the gate mounts
    // straight into it because the caller has already verified there
    // is at least one item to fetch.
    phase: 'downloading', // downloading | complete | error
    downloaded: 0,
    failed: 0,
    errorMessage: null,
  };

  _cancelled = false;
  _pollTimer = null;

  componentDidMount() {
    this.runDownloads();
  }

  componentWillUnmount() {
    this._cancelled = true;
    clearInterval(this._pollTimer);
  }

  async runDownloads() {
    const api = getLocalApiOrigin();
    const items = Array.isArray(this.props.items) ? this.props.items : [];
    if (items.length === 0) {
      this.setState({ phase: 'complete' });
      setTimeout(() => { if (!this._cancelled) this.props.onComplete?.(); }, 300);
      return;
    }

    try {
      const downloadRes = await fetch(`${api}/api/assets/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const downloadStatus = await downloadRes.json();

      if (downloadStatus.status === 'complete') {
        this.setState({ phase: 'complete', downloaded: items.length });
        setTimeout(() => { if (!this._cancelled) this.props.onComplete?.(); }, 600);
        return;
      }

      this._pollTimer = setInterval(() => this.pollProgress(items.length), 500);
    } catch (err) {
      if (this._cancelled) return;
      this.setState({ phase: 'error', errorMessage: err?.message || t('Network error.') });
    }
  }

  async pollProgress(total) {
    try {
      const api = getLocalApiOrigin();
      const res = await fetch(`${api}/api/assets/status`);
      const status = await res.json();
      if (this._cancelled) return;

      this.setState({
        downloaded: status.downloaded || 0,
        failed: status.failed || 0,
      });

      if (status.done) {
        clearInterval(this._pollTimer);
        const failed = status.failed || 0;
        if (failed === total) {
          this.setState({ phase: 'error', errorMessage: t('Could not download any updated assets.') });
        } else {
          this.setState({ phase: 'complete' });
          setTimeout(() => { if (!this._cancelled) this.props.onComplete?.(); }, 600);
        }
      }
    } catch {}
  }

  render() {
    const items = Array.isArray(this.props.items) ? this.props.items : [];
    const total = items.length;
    const { phase, downloaded, failed, errorMessage } = this.state;
    const progress = total > 0 ? Math.min(100, Math.round((downloaded / total) * 100)) : 0;
    const remaining = Math.max(0, total - downloaded - failed);

    return (
      <div className="fixed inset-0 z-[95] grid place-items-center" style={{ background: BG_ATMOSPHERE }}>
        <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        <div className="relative w-full max-w-[32cqw] p-8" style={{ ...DIALOG_STYLE }}>
          <FourCorners />

          <div className="text-center">
            <img src="/valkenhall-logo.png" alt="Valkenhall" className="h-16 mx-auto mb-6" draggable={false} />

            {phase === 'downloading' && (
              <>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>{t('Updating Assets')}</h2>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>
                  {t('New artwork has arrived. Pulling it down so your collection stays current.')}
                </p>

                <div
                  className="w-full h-3 rounded-full overflow-hidden mb-3"
                  style={{ background: `${GOLD} 0.08)`, border: `1px solid ${GOLD} 0.12)` }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${progress}%`,
                      background: 'linear-gradient(90deg, #8b6914, #d4a843, #c49a38)',
                      boxShadow: '0 0 8px rgba(212,168,67,0.3)',
                    }}
                  />
                </div>

                <div className="flex items-center justify-between text-xs" style={{ color: TEXT_MUTED }}>
                  <span>{t('{done} / {total} images', { done: downloaded, total })}</span>
                  <span className="font-bold" style={{ color: ACCENT_GOLD }}>{progress}%</span>
                </div>

                {remaining > 0 && (
                  <p className="text-xs mt-3" style={{ color: TEXT_MUTED }}>
                    {t('{n} remaining', { n: remaining })}
                    {failed > 0 && <span style={{ color: '#c45050' }}> · {t('{n} failed', { n: failed })}</span>}
                  </p>
                )}

                {downloaded === 0 && failed === 0 && (
                  <div className="flex justify-center mt-4">
                    <RuneSpinner size={36} />
                  </div>
                )}
              </>
            )}

            {phase === 'complete' && (
              <>
                <div className="text-4xl mb-4">⚔️</div>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: TEXT_PRIMARY }}>{t('Updated')}</h2>
                <p className="text-sm" style={{ color: TEXT_MUTED }}>
                  {failed > 0
                    ? t('{done} updated, {failed} could not be reached.', { done: downloaded, failed })
                    : t('All new artwork is ready. Entering the arena...')}
                </p>
              </>
            )}

            {phase === 'error' && (
              <>
                <h2 className="text-xl font-bold arena-heading mb-2" style={{ color: '#c45050' }}>{t('Update Failed')}</h2>
                <p className="text-sm mb-4" style={{ color: TEXT_MUTED }}>{errorMessage || t('Could not download updated assets.')}</p>
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    className="px-5 py-2 text-sm font-semibold arena-heading cursor-pointer transition-all"
                    style={{
                      background: 'linear-gradient(180deg, rgba(212,168,67,0.9) 0%, rgba(160,120,40,0.9) 100%)',
                      border: '1px solid rgba(228,200,100,0.6)',
                      borderRadius: 'var(--radius-md)',
                      color: '#1a1408',
                    }}
                    onClick={() => {
                      this.setState({ phase: 'downloading', downloaded: 0, failed: 0, errorMessage: null });
                      this.runDownloads();
                    }}
                  >
                    {t('Retry')}
                  </button>
                  <button
                    type="button"
                    className="px-5 py-2 text-sm cursor-pointer transition-all"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0.12) 100%)',
                      border: `1px solid ${GOLD} 0.3)`,
                      borderRadius: 'var(--radius-md)',
                      color: TEXT_BODY,
                    }}
                    onClick={() => this.props.onComplete?.()}
                  >
                    {t('Skip for Now')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
